import { useCallback, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import PortalConnection from 'models/PortalConnection';
import { indexPortalConnections, portalConnectionQueryKeys } from 'services/api/portalConnections';

// Stable identity for the unresolved state — a fresh [] on every render would invalidate
// downstream memos and effects.
const EMPTY_CONNECTIONS: PortalConnection[] = [];

/**
 * The acting client's Connected Portals. One query key for the whole app (the rows are derived
 * per acting client and take no parameters), so every surface that needs a portal's mode,
 * recordings, scan history, or readiness shares one cache entry rather than fetching the same
 * list under a name of its own.
 */
export function usePortalConnections() {
  const query = useQuery({
    queryKey: portalConnectionQueryKeys.list(),
    queryFn: ({ signal }) => indexPortalConnections(signal),
    select: (data) => data.map((options) => new PortalConnection(options)),
  });

  const connections = query.data ?? EMPTY_CONNECTIONS;
  const bySourceSystem = useMemo(
    () => new Map(connections.map((connection) => [connection.sourceSystem, connection])),
    [connections]
  );

  const connectionFor = useCallback(
    (sourceSystem?: string | null) => (sourceSystem ? (bySourceSystem.get(sourceSystem) ?? null) : null),
    [bySourceSystem]
  );

  return { connections, connectionFor, isPending: query.isPending, isError: query.isError };
}

// The identifier as it reads before the connections load, or for a portal this client has no
// configured row for ("careport" -> "Careport"). The server owns the real name (`displayName`);
// this is the gap-filler, not a second catalog.
export function fallbackPortalName(sourceSystem: string): string {
  return sourceSystem.charAt(0).toUpperCase() + sourceSystem.slice(1);
}

/**
 * THE way a portal's name reaches the screen: the server's `displayName`, falling back to the
 * identifier while the list is in flight. Returns a function rather than a string so one query
 * serves a whole table of rows.
 */
export function usePortalName() {
  const { connectionFor } = usePortalConnections();

  return useCallback(
    (sourceSystem?: string | null) => {
      if (!sourceSystem) return '';
      return connectionFor(sourceSystem)?.displayName ?? fallbackPortalName(sourceSystem);
    },
    [connectionFor]
  );
}
